import type * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import Textarea from '@cloudscape-design/components/textarea';
import {
  ALL_COURTESY_MIGRATION_STAGES,
  AuditCourtesyMigrationOutput,
  CourtesyMigrationStage,
  MigrateCourtesyOutput,
  stageRequiresExplicitTenants,
  type CourtesyAuditRow,
} from '@etiquetachef/types';
import {
  auditCourtesyMigrationCallable,
  migrateCourtesyCallable,
  restoreLegacyCourtesySubscriptionCallable,
} from '@/lib/callables';
import { reportNonFatal } from '@/lib/errorReport';
import {
  auditMarkdown,
  migrationMarkdown,
  simulationKey,
} from '@/features/billing/courtesyMigrationReport';

type AuditState =
  | { kind: 'LOADING' }
  | { kind: 'READY'; audit: AuditCourtesyMigrationOutput }
  | { kind: 'ERROR' };

type LastRun = { key: string; out: MigrateCourtesyOutput; reason: string };

/** Mensagem legível de um erro qualquer (callable, rede, parse). */
function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Tela `/billing/courtesy-migration` — migração da cortesia e dos demos para
 * o tipo comercial, em etapas (E1 MARK/MARK_DIRECT → E2 observar → E3
 * CANCEL_GATEWAY → E4 CLEAR_LEGACY_ID).
 *
 * Origem: #738 — commit cac843787, 2026-09-25 11:24 +0200 (#679 F7).
 *
 * Por quê: a migração mexe em assinatura de gateway de tenant real, e o
 * rollback só existe entre E3 e E4 (depois do E4 o id legado sai do doc). A tela obriga o caminho seguro: "Aplicar"
 * só fica disponível depois de uma simulação da MESMA etapa, com os MESMOS
 * tenants e o mesmo build mínimo (`simulationKey`) — o operador aplica
 * exatamente o que acabou de ver. O relatório de cada passo sai em Markdown
 * para colar na #679.
 *
 * Contrato: ao montar chama `auditCourtesyMigration` (quem está em qual
 * etapa); "Simular"/"Aplicar" chamam `migrateCourtesy` com `dryRun`
 * true/false e o motivo; com um único tenant selecionado entre E3 e E4
 * (legado marcado e ainda no doc), oferece `restoreLegacyCourtesySubscription`
 * (rollback; o servidor confere o mesmo em `planRestoreLegacy`). Respostas validadas com
 * `AuditCourtesyMigrationOutput`/`MigrateCourtesyOutput`. Falhas →
 * `reportNonFatal` (`admin_courtesy_migration_audit_failed`,
 * `…_run_failed`, `…_restore_failed`) + mensagem na tela. Depois de aplicar ou
 * restaurar, recarrega a auditoria.
 *
 * Usado por: rota `billing/courtesy-migration` (`routes/router.tsx`), menu
 * Cobrança (`AdminLayout`). Spec: `docs/screens/admin-courtesy-migration.md`.
 *
 * Arquitetura: toda a decisão (elegibilidade por etapa, escrita em
 * `billing/subscription`, cancelamento no gateway) é das callables; a tela só
 * conduz e mostra. As etapas vêm de `CourtesyMigrationStage` (constante do
 * conceito, #761), nunca de string solta.
 *
 * Issues: #679 (F7), #738 (migração em etapas), #761 (constantes de conceito).
 */
export function CourtesyMigrationPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [audit, setAudit] = useState<AuditState>({ kind: 'LOADING' });
  const [selected, setSelected] = useState<CourtesyAuditRow[]>([]);
  const [stage, setStage] = useState<CourtesyMigrationStage>(CourtesyMigrationStage.MARK);
  const [reason, setReason] = useState('');
  const [minAndroid, setMinAndroid] = useState('');
  const [minIos, setMinIos] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<LastRun | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);

  const loadAudit = useCallback((): void => {
    setAudit({ kind: 'LOADING' });
    auditCourtesyMigrationCallable({})
      .then((raw) => {
        const parsed = AuditCourtesyMigrationOutput.safeParse(raw);
        if (!parsed.success) throw new Error('auditCourtesyMigration: resposta fora do contrato');
        setAudit({ kind: 'READY', audit: parsed.data });
        // A seleção guarda linhas da auditoria anterior: refaz pelos ids, com
        // os dados novos, e larga quem saiu do relatório — senão o botão de
        // restaurar lê o estado velho e um tenant invisível entra no lote.
        const byId = new Map(parsed.data.rows.map((r) => [r.tenantId, r]));
        setSelected((prev) =>
          prev.flatMap((r) => {
            const fresh = byId.get(r.tenantId);
            return fresh ? [fresh] : [];
          }),
        );
      })
      .catch((err: unknown) => {
        reportNonFatal('admin_courtesy_migration_audit_failed', { message: toMessage(err) });
        setAudit({ kind: 'ERROR' });
      });
  }, []);

  useEffect(() => {
    loadAudit();
  }, [loadAudit]);

  const tenantIds = useMemo(() => selected.map((r) => r.tenantId), [selected]);
  const isBuild = (v: string): boolean => Number.isInteger(Number(v)) && Number(v) > 0;
  const minBuilds =
    stage === CourtesyMigrationStage.CLEAR_LEGACY_ID && isBuild(minAndroid) && isBuild(minIos)
      ? { android: Number(minAndroid), ios: Number(minIos) }
      : undefined;
  const currentKey = simulationKey({ stage, tenantIds, minBuilds });
  const needsList = stageRequiresExplicitTenants(stage);
  const inputReady =
    reason.trim().length >= 10 &&
    (!needsList || tenantIds.length > 0) &&
    (stage !== CourtesyMigrationStage.CLEAR_LEGACY_ID || minBuilds !== undefined);
  const simulated =
    lastRun !== null && lastRun.out.dryRun && lastRun.key === currentKey && lastRun.out.rows.length > 0;

  async function run(dryRun: boolean): Promise<void> {
    setRunning(true);
    setError(null);
    try {
      const raw = await migrateCourtesyCallable({
        stage,
        tenantIds,
        dryRun,
        reason: reason.trim(),
        ...(minBuilds ? { minBuilds } : {}),
      });
      const parsed = MigrateCourtesyOutput.safeParse(raw);
      if (!parsed.success) throw new Error('migrateCourtesy: resposta fora do contrato');
      setLastRun({ key: currentKey, out: parsed.data, reason: reason.trim() });
      if (!dryRun) loadAudit();
    } catch (err) {
      reportNonFatal('admin_courtesy_migration_run_failed', { stage, dryRun, message: toMessage(err) });
      setError(toMessage(err));
    } finally {
      setRunning(false);
      setConfirmOpen(false);
    }
  }

  // Rollback da E3 só entre E3 e E4: legado marcado e ainda no doc (o servidor
  // confere o mesmo em `planRestoreLegacy`, e ainda que o legado esteja
  // cancelado no Stripe).
  const single = selected.length === 1 ? selected[0] : undefined;
  const restoreTarget =
    single?.legacyExternalSubscriptionId != null &&
    single.externalSubscriptionId === single.legacyExternalSubscriptionId
      ? single
      : null;

  async function restore(): Promise<void> {
    if (!restoreTarget) return;
    setRunning(true);
    setError(null);
    try {
      await restoreLegacyCourtesySubscriptionCallable({
        tenantId: restoreTarget.tenantId,
        reason: reason.trim(),
      });
      loadAudit();
    } catch (err) {
      reportNonFatal('admin_courtesy_migration_restore_failed', { message: toMessage(err) });
      setError(toMessage(err));
    } finally {
      setRunning(false);
      setRestoreOpen(false);
    }
  }

  const stageOptions = ALL_COURTESY_MIGRATION_STAGES.map((s) => ({
    value: s,
    label: t(`courtesyMigration.stages.${s}.label`),
    description: t(`courtesyMigration.stages.${s}.description`),
  }));

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('courtesyMigration.subtitle')}>
          {t('courtesyMigration.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Alert type="warning">{t('courtesyMigration.invariant')}</Alert>

        <Container
          header={
            <Header
              variant="h2"
              description={t('courtesyMigration.audit.description')}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {audit.kind === 'READY' ? (
                    <CopyToClipboard
                      variant="button"
                      textToCopy={auditMarkdown(audit.audit)}
                      copyButtonText={t('courtesyMigration.copyReport')}
                      copySuccessText={t('common.copied')}
                      copyErrorText={t('common.copyError')}
                    />
                  ) : null}
                  <Button iconName="refresh" onClick={loadAudit} disabled={audit.kind === 'LOADING'}>
                    {t('courtesyMigration.audit.refresh')}
                  </Button>
                </SpaceBetween>
              }
            >
              {t('courtesyMigration.audit.title')}
            </Header>
          }
        >
          {audit.kind === 'LOADING' ? (
            <Box textAlign="center" padding="m">
              <Spinner />
            </Box>
          ) : audit.kind === 'ERROR' ? (
            <Alert type="error">{t('courtesyMigration.audit.errorLoad')}</Alert>
          ) : (
            <SpaceBetween size="s">
              <Table<CourtesyAuditRow>
                items={audit.audit.rows}
                trackBy="tenantId"
                variant="embedded"
                selectionType="multi"
                selectedItems={selected}
                onSelectionChange={({ detail }): void => {
                  setSelected(detail.selectedItems);
                }}
                ariaLabels={{
                  itemSelectionLabel: (_, item): string => item.tenantId,
                  allItemsSelectionLabel: (): string => t('courtesyMigration.audit.selectAll'),
                }}
                empty={<Box textAlign="center">{t('courtesyMigration.audit.empty')}</Box>}
                columnDefinitions={[
                  {
                    id: 'tenant',
                    header: t('courtesyMigration.columns.tenant'),
                    cell: (r): React.ReactNode => (
                      <SpaceBetween size="xxs">
                        <span>{r.tenantName}</span>
                        <Box variant="code">{r.tenantId}</Box>
                      </SpaceBetween>
                    ),
                  },
                  {
                    id: 'mode',
                    header: t('courtesyMigration.columns.mode'),
                    cell: (r): string =>
                      r.storedMode ?? t('courtesyMigration.modeLegacy', { mode: r.effectiveMode }),
                  },
                  {
                    id: 'gateway',
                    header: t('courtesyMigration.columns.gateway'),
                    cell: (r): React.ReactNode =>
                      r.externalSubscriptionId === null ? (
                        '—'
                      ) : (
                        <SpaceBetween size="xxs">
                          <Box variant="code">{r.externalSubscriptionId}</Box>
                          <span>{r.gatewayStatus ?? t('courtesyMigration.gatewayMissing')}</span>
                        </SpaceBetween>
                      ),
                  },
                  {
                    id: 'legacyGate',
                    header: t('courtesyMigration.columns.legacyGate'),
                    cell: (r): React.ReactNode => (
                      <StatusIndicator type={r.legacyGateOpen ? 'success' : 'error'}>
                        {t(r.legacyGateOpen ? 'courtesyMigration.open' : 'courtesyMigration.closed')}
                      </StatusIndicator>
                    ),
                  },
                  {
                    id: 'newGate',
                    header: t('courtesyMigration.columns.newGate'),
                    cell: (r): React.ReactNode => (
                      <StatusIndicator type={r.newGateOpen ? 'success' : 'error'}>
                        {t(r.newGateOpen ? 'courtesyMigration.open' : 'courtesyMigration.closed')}
                      </StatusIndicator>
                    ),
                  },
                  {
                    id: 'devices',
                    header: t('courtesyMigration.columns.devices'),
                    cell: (r): string =>
                      r.devices.length === 0
                        ? '—'
                        : r.devices.map((d) => `${d.platform} ${d.appVersion || '?'}`).join(', '),
                  },
                ]}
              />
              <Box color="text-body-secondary">
                {t('courtesyMigration.audit.directWithoutMode', {
                  count: audit.audit.directWithoutStoredMode.length,
                })}
              </Box>
            </SpaceBetween>
          )}
        </Container>

        <Container
          header={
            <Header variant="h2" description={t('courtesyMigration.run.description')}>
              {t('courtesyMigration.run.title')}
            </Header>
          }
        >
          <SpaceBetween size="m">
            <FormField label={t('courtesyMigration.run.stage')}>
              <Select
                selectedOption={stageOptions.find((o) => o.value === stage) ?? null}
                options={stageOptions}
                onChange={({ detail }): void => {
                  setStage(detail.selectedOption.value as CourtesyMigrationStage);
                }}
              />
            </FormField>
            <Box>
              {tenantIds.length === 0
                ? t(
                    needsList
                      ? 'courtesyMigration.run.needsSelection'
                      : 'courtesyMigration.run.allEligible',
                  )
                : t('courtesyMigration.run.selected', { tenants: tenantIds.join(', ') })}
            </Box>
            {stage === CourtesyMigrationStage.CLEAR_LEGACY_ID ? (
              <ColumnLayout columns={2}>
                <FormField label={t('courtesyMigration.run.minAndroid')}>
                  <Input
                    type="number"
                    value={minAndroid}
                    onChange={({ detail }): void => {
                      setMinAndroid(detail.value);
                    }}
                  />
                </FormField>
                <FormField label={t('courtesyMigration.run.minIos')}>
                  <Input
                    type="number"
                    value={minIos}
                    onChange={({ detail }): void => {
                      setMinIos(detail.value);
                    }}
                  />
                </FormField>
              </ColumnLayout>
            ) : null}
            <FormField
              label={t('courtesyMigration.run.reason')}
              constraintText={t('courtesyMigration.run.reasonHint')}
            >
              <Textarea
                value={reason}
                onChange={({ detail }): void => {
                  setReason(detail.value);
                }}
              />
            </FormField>
            {error ? <Alert type="error">{error}</Alert> : null}
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                disabled={!inputReady || running}
                loading={running && !confirmOpen}
                onClick={(): void => {
                  void run(true);
                }}
              >
                {t('courtesyMigration.run.simulate')}
              </Button>
              <Button
                variant="primary"
                disabled={!inputReady || !simulated || running}
                onClick={(): void => {
                  setConfirmOpen(true);
                }}
              >
                {t('courtesyMigration.run.apply')}
              </Button>
              <Button
                disabled={restoreTarget === null || reason.trim().length === 0 || running}
                onClick={(): void => {
                  setRestoreOpen(true);
                }}
              >
                {t('courtesyMigration.restore.button')}
              </Button>
            </SpaceBetween>
            {!simulated && inputReady ? (
              <Box color="text-body-secondary">{t('courtesyMigration.run.simulateFirst')}</Box>
            ) : null}
          </SpaceBetween>
        </Container>

        {lastRun ? (
          <Container
            header={
              <Header
                variant="h2"
                actions={
                  <CopyToClipboard
                    variant="button"
                    textToCopy={migrationMarkdown(lastRun.out, lastRun.reason)}
                    copyButtonText={t('courtesyMigration.copyReport')}
                    copySuccessText={t('common.copied')}
                    copyErrorText={t('common.copyError')}
                  />
                }
              >
                {t(
                  lastRun.out.dryRun
                    ? 'courtesyMigration.result.simulated'
                    : 'courtesyMigration.result.applied',
                  { stage: lastRun.out.stage },
                )}
              </Header>
            }
          >
            <SpaceBetween size="s">
              <Table
                items={lastRun.out.rows}
                trackBy="tenantId"
                variant="embedded"
                empty={<Box textAlign="center">{t('courtesyMigration.result.empty')}</Box>}
                columnDefinitions={[
                  {
                    id: 'tenant',
                    header: t('courtesyMigration.columns.tenant'),
                    cell: (r): string => r.tenantId,
                  },
                  {
                    id: 'after',
                    header: t('courtesyMigration.result.after'),
                    cell: (r): React.ReactNode => <Box variant="code">{JSON.stringify(r.after)}</Box>,
                  },
                  {
                    id: 'gateway',
                    header: t('courtesyMigration.columns.gateway'),
                    cell: (r): string => r.gateway ?? '—',
                  },
                ]}
              />
              {lastRun.out.skipped.length > 0 ? (
                <Box>
                  {t('courtesyMigration.result.skipped')}:{' '}
                  {lastRun.out.skipped.map((s) => `${s.tenantId} (${s.why})`).join(', ')}
                </Box>
              ) : null}
              {lastRun.out.failed.length > 0 ? (
                <Alert type="error">
                  {lastRun.out.failed.map((f) => `${f.tenantId}: ${f.error}`).join('; ')}
                </Alert>
              ) : null}
            </SpaceBetween>
          </Container>
        ) : null}
      </SpaceBetween>

      <Modal
        visible={confirmOpen}
        onDismiss={(): void => {
          setConfirmOpen(false);
        }}
        header={t('courtesyMigration.confirm.title', { stage })}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={(): void => {
                  setConfirmOpen(false);
                }}
              >
                {t('tenantDetail.actions.cancel')}
              </Button>
              <Button
                variant="primary"
                loading={running}
                onClick={(): void => {
                  void run(false);
                }}
              >
                {t('courtesyMigration.confirm.action')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {t('courtesyMigration.confirm.body', { count: lastRun?.out.rows.length ?? 0 })}
      </Modal>

      <Modal
        visible={restoreOpen}
        onDismiss={(): void => {
          setRestoreOpen(false);
        }}
        header={t('courtesyMigration.restore.title')}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={(): void => {
                  setRestoreOpen(false);
                }}
              >
                {t('tenantDetail.actions.cancel')}
              </Button>
              <Button
                variant="primary"
                loading={running}
                onClick={(): void => {
                  void restore();
                }}
              >
                {t('courtesyMigration.restore.action')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {t('courtesyMigration.restore.body', { tenant: restoreTarget?.tenantId ?? '' })}
      </Modal>
    </ContentLayout>
  );
}
